// API response parsing starts
const buildCategoryData = (entities, item) => {
    const slug = item.slug
    entities[slug] = {
        slug,
        name: item.name,
        level: item.level,
        children: item.children
            ? item.children.slice(1).map((child) => buildCategoryData(entities, child))
            : [],
    }
    return slug
}

const parseCategoryLinks = (children = []) => {
    const entities = {}
    const baseCategories = children.map((child) => buildCategoryData(entities, child))
    return {
        entities,
        categories: baseCategories,
    }
}

// API response parsing ends

// Convert from { entities, categories } to { parent, children } starts

const renderObj = (item) => ({
    slug: item.slug,
    level: item.level,
    name: item.name,
})

// Convert from { entities, categories } to { parent, children } ends

// Internal parsing - from { parent, children } to { parent, children } (with breakpoints) starts

const rule = {
    pivot: 18,
    lower: 10,
    upper: 18,
    col_min_limit: 2,
    col_max_limit: 5,
}

const parseChildrenData = (children) => {
    let breakPoint = children.length % 2 === 0 ? children.length / 2 : (children.length + 1) / 2
    breakPoint = children.length < 10 ? 0 : breakPoint
    let categoryBreakPoint = 0
    if (breakPoint) {
        for (let i = breakPoint - 1; i < children.length; i++) {
            if (children[i].level === 2) {
                categoryBreakPoint = i
                break
            }
        }
    }
    const parsedChildren = []
    parsedChildren.push(children.slice(0, categoryBreakPoint))
    parsedChildren.push(children.slice(categoryBreakPoint))
    try {
        const { pivot } = rule
        if (
            parsedChildren[0].length === 0 &&
            parsedChildren[1].length >= 10 &&
            parsedChildren[1].length <= 25
        ) {
            let break_point = 0
            for (let i = children.length - 1; i >= 0; i--) {
                if (children[i].level === 2) {
                    break_point = i
                    break
                }
            }
            const percentage = (break_point / (children.length - break_point || 1)) * 100
            if (percentage >= 40) {
                return [children.slice(0, break_point), children.slice(break_point)]
            }
        }
        if (parsedChildren[0].length <= 13 && parsedChildren[1].length <= 13) {
            return parsedChildren
        }

        for (let pivot_p = pivot; pivot_p <= 30; pivot_p++) {
            const candidate = getBestCandidate(children, pivot_p)
            if (candidate.length > 0) {
                return candidate
            }
        }
    } catch (e) {
        console.log(e)
    }
    return parsedChildren
}

const getBestCandidate = (children, pivot) => {
    const { lower, upper, col_min_limit, col_max_limit } = rule
    let bestCandidate = []
    let bestRank = Number.MAX_VALUE
    for (let point = lower; point <= upper; point++) {
        const candidate = divideOnPivot(children, point)
        const currentRank = evaluate(candidate)
        const maxColHeight =
            candidate.length === 0
                ? Number.MAX_VALUE
                : candidate.length === 1
                ? candidate[0].length
                : candidate.reduce(function (maximum, item) {
                      return maximum > item.length ? maximum : item.length
                  }, 0)
        if (
            bestRank > currentRank &&
            maxColHeight <= pivot &&
            candidate.length <= col_max_limit &&
            candidate.length >= col_min_limit
        ) {
            bestRank = currentRank
            bestCandidate = candidate
        }
    }
    if (bestCandidate.length > 1 && bestCandidate[bestCandidate.length - 1].length < 3) {
        bestCandidate[bestCandidate.length - 2] = [
            ...bestCandidate[bestCandidate.length - 2],
            ...bestCandidate[bestCandidate.length - 1],
        ]
        bestCandidate.pop()
    }
    return bestCandidate
}

const evaluate = (data) => {
    if (!Array.isArray(data) || data.length === 0) {
        return Number.MAX_VALUE
    }
    if (data.length === 1) {
        return 0
    }
    const list = data.map((item) => +item.length)
    const sum = list.reduce(function (cum, item) {
        return cum + item
    }, 0)
    const mean = sum / list.length
    const squareList = list.map((item) => (item - mean) * (item - mean))
    const squareSum = squareList.reduce(function (sqCum, item) {
        return sqCum + item
    }, 0)

    return squareSum / list.length
}

const divideOnPivot = (children, pivot) => {
    let breakPoint = pivot
    const size = pivot
    const parsedChildren = []
    let nextBreakPoint = 0
    for (let i = 0; i < children.length; i++) {
        if (children[i].level === 2 && i >= breakPoint) {
            parsedChildren.push(children.slice(nextBreakPoint, i))
            nextBreakPoint = i
            breakPoint = i + size
        }
    }
    parsedChildren.push(children.slice(nextBreakPoint))
    return parsedChildren
}

// Internal parsing - from { parent, children } to { parent, children } (with breakpoints) ends

// STEP 1
const getFlattenData = (response) => {
    const flattenRespObj = parseCategoryLinks(response.data?.udp_tree)
    return flattenRespObj
}

// STEP 2
const parseDesktopUdpData = ({ entities, categories }) =>
    categories
        ? categories.map((category) => {
              const l1Data = entities[category]
              const children = l1Data.children.reduce((acc, l2Slug) => {
                  const l2Data = entities[l2Slug]
                  return acc.concat(
                      renderObj(l2Data),
                      ...l2Data.children.map((l3Slug) => renderObj(entities[l3Slug]))
                  )
              }, [])
              return {
                  parent: renderObj(l1Data),
                  children,
              }
          })
        : []

// STEP 3
const getUdpParsedData = (udpData) => {
    const parsedData = udpData.map((data) => ({
        parent: data.parent,
        children: parseChildrenData(data.children),
    }))
    return parsedData
}

export const getCategoryTreeData = (response) => {
    response.data.udp_tree.unshift({
        name: "Health Resource Center",
        level: 1,
        children: [
            {
                name: "To be ignored",
            },
            {
                name: "All Diseases",
                slug: "/all-diseases",
                level: 2,
            },
            {
                name: "All Medicines",
                slug: "/drugs-all-medicines",
                level: 2,
            },
            {
                name: "Medicines by Therapeutic Class",
                slug: "/drugs-therapeutic-classes",
                level: 2,
            },
        ],
    })
    const flattenData = getFlattenData(response)
    const desktopUdpData = parseDesktopUdpData(flattenData)
    const finalData = getUdpParsedData(desktopUdpData)
    return finalData
}
